import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    MenuItem,
    CircularProgress,
    Paper,
    InputAdornment,
} from '@mui/material';
import {
    Calculate,
    Restaurant,
    People,
    ArrowForward,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { TopBar } from '../layout/TopBar';
import { useNavigate } from 'react-router-dom';
import { getUserRecipes, Recipe, RecipeIngredient } from '../services/recipesService';
import { BottomNavigation } from '../layout/BottomNavigation';

export const PortionCalculatorPage: React.FC = () => {
    const { theme } = useTheme();
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    const [recipes, setRecipes] = useState<Recipe[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedRecipeId, setSelectedRecipeId] = useState('');
    const [desiredServings, setDesiredServings] = useState<string>('');

    const selectedRecipe = recipes.find(r => r.id === selectedRecipeId);

    useEffect(() => {
        const loadRecipes = async () => {
            if (!user?.uid) return;
            try {
                const data = await getUserRecipes(user.uid);
                setRecipes(data.sort((a, b) => a.title.localeCompare(b.title)));
            } catch (error) {
                console.error('Error loading recipes', error);
            } finally {
                setLoading(false);
            }
        };
        loadRecipes();
    }, [user]);

    const calculateAmount = (quantity: string | number, originalServings: string | number, newServings: string | number) => {
        const original = typeof originalServings === 'string' ? parseFloat(originalServings) : originalServings;
        const desired = typeof newServings === 'string' ? parseFloat(newServings) : newServings;

        let qty: number;
        if (typeof quantity === 'string') {
            qty = parseFloat(quantity.replace(',', '.'));
        } else {
            qty = quantity;
        }

        if (isNaN(original) || isNaN(desired) || isNaN(qty) || original === 0) return quantity;

        const factor = desired / original;
        const newAmount = qty * factor;

        // Format logical display
        if (Number.isInteger(newAmount)) return newAmount.toString();
        return newAmount.toFixed(1).replace('.', ',');
    };

    if (authLoading) return <CircularProgress />;

    return (
        <>
            <TopBar
                title="Calculadora de Porções"
                icon={<Calculate sx={{ fontSize: '0.875rem' }} />}
                showBackButton={true}
                showProfileButton={true}
                onBackClick={() => navigate('/recipes')}
            />

            <Box sx={{
                paddingBottom: '120px',
                paddingTop: '80px',
                px: 2,
                maxWidth: '800px',
                margin: '0 auto',
                minHeight: '100vh',
                backgroundColor: theme.colors.background
            }}>

                <Paper elevation={0} sx={{
                    p: 3,
                    borderRadius: theme.radii.medium,
                    backgroundColor: theme.colors.card,
                    border: `1px solid ${theme.colors.border}`,
                    mb: 3
                }}>
                    <Typography variant="h6" sx={{ mb: 2, color: theme.colors.text.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Restaurant fontSize="small" color="primary" /> Selecione a Receita
                    </Typography>

                    <TextField
                        select
                        fullWidth
                        label="Escolha uma receita"
                        value={selectedRecipeId}
                        onChange={(e) => {
                            setSelectedRecipeId(e.target.value);
                            const r = recipes.find(rec => rec.id === e.target.value);
                            if (r) setDesiredServings(r.servings.toString());
                        }}
                        disabled={loading}
                        sx={{ mb: 3 }}
                    >
                        {loading ? <MenuItem disabled>Carregando...</MenuItem> : null}
                        {!loading && recipes.length === 0 ? <MenuItem disabled>Nenhuma receita encontrada</MenuItem> : null}
                        {recipes.map((recipe) => (
                            <MenuItem key={recipe.id} value={recipe.id}>
                                {recipe.title}
                            </MenuItem>
                        ))}
                    </TextField>

                    {selectedRecipe && (
                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                            <Box sx={{ flex: 1 }}>
                                <TextField
                                    label="Porções Originais"
                                    value={selectedRecipe.servings}
                                    fullWidth
                                    disabled
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><People fontSize="small" /></InputAdornment>
                                    }}
                                />
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                                <ArrowForward color="action" />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <TextField
                                    label="Desejo fazer"
                                    value={desiredServings}
                                    onChange={(e) => setDesiredServings(e.target.value)}
                                    fullWidth
                                    type="number"
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><People color="primary" fontSize="small" /></InputAdornment>
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                </Paper>

                {selectedRecipe && (
                    <Paper elevation={0} sx={{
                        p: 3,
                        borderRadius: theme.radii.medium,
                        backgroundColor: theme.colors.card,
                        border: `1px solid ${theme.colors.border}`
                    }}>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" sx={{ color: theme.colors.text.primary, fontWeight: 'bold' }}>
                                Ingredientes Calculados
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mt: 0.5 }}>
                                Baseado em <strong>{desiredServings || 0}</strong> porções
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                            {selectedRecipe.ingredients.map((ing: RecipeIngredient, index) => {
                                const calculated = calculateAmount(ing.quantity, selectedRecipe.servings, desiredServings);
                                return (
                                    <Box key={index} sx={{
                                        p: 2,
                                        borderRadius: theme.radii.small,
                                        bgcolor: theme.colors.background,
                                        border: `1px solid ${theme.colors.border}`,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <Typography sx={{ fontWeight: 500, color: theme.colors.text.primary, flex: 1 }}>
                                            {ing.name}
                                        </Typography>
                                        <Box sx={{ textAlign: 'right', minWidth: '80px' }}>
                                            <Typography sx={{ fontWeight: 'bold', color: theme.colors.primary, fontSize: '1.1rem' }}>
                                                {calculated} <Typography component="span" variant="caption" sx={{ color: theme.colors.text.secondary }}>{ing.unit}</Typography>
                                            </Typography>
                                            {(calculated !== ing.quantity) && (typeof ing.quantity !== 'string') && (
                                                <Typography variant="caption" sx={{ color: theme.colors.text.secondary, textDecoration: 'line-through', opacity: 0.7 }}>
                                                    {ing.quantity} {ing.unit}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                )}

            </Box>
            <BottomNavigation />
        </>
    );
};
